/**
 * StatusQuickEdit — clickable status badge with an inline dropdown menu that
 * persists the change via PATCH /api/products/{id}. Used in the admin product
 * list to avoid opening the full edit form just to flip a piece between
 * Disponível ↔ Pausada ↔ Vendida.
 *
 * UX rules:
 *   • Selecting "Vendida" shows an inline confirmation popover before
 *     committing (sold removes the piece from the public catalogue, so the
 *     action deserves a heartbeat of friction).
 *   • Other status changes commit immediately.
 *   • After a successful save the badge briefly flashes (1s) so the admin
 *     sees that the change persisted.
 *   • The component is fully self-contained — parent passes current status
 *     and a callback that receives the new status after the server confirmed.
 */
import { useEffect, useRef, useState } from "react";
import { api } from "../../lib/api";
import { ChevronDown, Check } from "lucide-react";

const OPTIONS = [
  { value: "available", label: "Disponível" },
  { value: "paused", label: "Pausada" },
  { value: "sold", label: "Vendida" },
];

const BADGE_CLASS = {
  available: "bg-[#E8F0E4] text-[#3E6A1E]",
  sold: "bg-[#F1E0DC] text-[#A8503B]",
  paused: "bg-[#F0E9DA] text-[#8C7B5E]",
  unique: "bg-[#E8F0E4] text-[#3E6A1E]", // legacy alias for available
};

const BADGE_LABEL = {
  available: "Disponível",
  paused: "Pausada",
  sold: "Vendida",
  unique: "Disponível",
};

export default function StatusQuickEdit({ productId, status, onChange }) {
  // current = optimistically reflects the latest committed status.
  // pendingSold = sold confirmation popover open (specific status).
  // open = dropdown open. flash = "just saved" highlight.
  // saving disables interactions while the PATCH is in flight.
  const [current, setCurrent] = useState(status);
  const [open, setOpen] = useState(false);
  const [pendingSold, setPendingSold] = useState(false);
  const [saving, setSaving] = useState(false);
  const [flash, setFlash] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => { setCurrent(status); }, [status]);

  // Close the dropdown / popover when the admin clicks outside, presses Esc,
  // or focuses elsewhere. Without this the popover would feel sticky.
  useEffect(() => {
    if (!open && !pendingSold) return;
    const onDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        setOpen(false);
        setPendingSold(false);
      }
    };
    const onKey = (e) => {
      if (e.key === "Escape") { setOpen(false); setPendingSold(false); }
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, pendingSold]);

  const commit = async (next) => {
    setSaving(true);
    try {
      await api.patch(`/products/${productId}`, { status: next });
      setCurrent(next);
      setFlash(true);
      // The flash class is removed after 1s by this timeout — purely cosmetic.
      setTimeout(() => setFlash(false), 1000);
      if (onChange) onChange(next);
    } catch (err) {
      const detail = err?.response?.data?.detail || "Não foi possível salvar";
      alert(`Erro ao atualizar status: ${detail}`);
    } finally {
      setSaving(false);
      setOpen(false);
      setPendingSold(false);
    }
  };

  const choose = (value) => {
    if (value === current) { setOpen(false); return; }
    if (value === "sold") {
      // Defer commit until the admin confirms via the popover.
      setPendingSold(true);
      setOpen(false);
      return;
    }
    commit(value);
  };

  const currentClass = BADGE_CLASS[current] || "bg-[#F5EFE8]";
  const currentLabel = BADGE_LABEL[current] || current;

  return (
    <div ref={rootRef} className="relative inline-block" data-testid={`status-quickedit-${productId}`}>
      <button
        type="button"
        disabled={saving}
        onClick={() => { if (!saving) { setOpen((v) => !v); setPendingSold(false); } }}
        className={
          "inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider px-2 py-1 transition-all duration-300 " +
          currentClass +
          (flash ? " ring-2 ring-[#B8A88A] ring-offset-1" : "") +
          (saving ? " opacity-60 cursor-wait" : " hover:opacity-80")
        }
        data-testid={`status-badge-${productId}`}
        data-flash={flash || undefined}
      >
        {currentLabel}
        <ChevronDown size={11} className={open ? "rotate-180 transition-transform" : "transition-transform"} />
      </button>

      {/* Dropdown menu — only options OTHER than the current status. */}
      {open && !pendingSold && (
        <div
          className="absolute z-30 left-0 mt-1 min-w-[150px] bg-white border border-[#E8E4DF] shadow-md py-1"
          data-testid={`status-menu-${productId}`}
        >
          {OPTIONS.filter((o) => o.value !== current).map((o) => (
            <button
              key={o.value}
              type="button"
              onClick={() => choose(o.value)}
              className="w-full text-left px-3 py-2 text-xs hover:bg-[#FAF9F7] flex items-center justify-between"
              data-testid={`status-option-${o.value}-${productId}`}
            >
              <span>{o.label}</span>
              <span className={"inline-block w-2 h-2 rounded-full " + BADGE_CLASS[o.value]} />
            </button>
          ))}
        </div>
      )}

      {/* Inline confirmation popover for "Vendida" — small, discreet, no modal. */}
      {pendingSold && (
        <div
          className="absolute z-30 left-0 mt-1 w-56 bg-white border border-[#E8E4DF] shadow-md p-3"
          data-testid={`status-confirm-sold-${productId}`}
        >
          <p className="text-xs text-[#1A1A1A] mb-3 leading-snug">
            Marcar como vendida?<br/>
            <span className="text-[10px] text-[#A8A8A8]">Esta peça sairá do catálogo público.</span>
          </p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => commit("sold")}
              disabled={saving}
              className="flex-1 inline-flex items-center justify-center gap-1 bg-[#A8503B] hover:bg-[#8C4030] text-white text-[10px] uppercase tracking-wider py-2 disabled:opacity-50"
              data-testid={`status-confirm-yes-${productId}`}
            >
              <Check size={11} /> Confirmar
            </button>
            <button
              type="button"
              onClick={() => setPendingSold(false)}
              disabled={saving}
              className="flex-1 border border-[#E8E4DF] text-[10px] uppercase tracking-wider py-2 hover:border-[#B8A88A]"
              data-testid={`status-confirm-no-${productId}`}
            >
              Cancelar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
